import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const scrollPositions = useRef(new Map());
  const isInitialLoad = useRef(true);
  const currentPath = useRef(null);

  // Disable browser's built-in scroll restoration
  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Save scroll position continuously as user scrolls
  useEffect(() => {
    let scrollTimer;
    
    const saveScrollPosition = () => {
      if (currentPath.current) {
        const scrollY = window.scrollY;
        if (scrollY > 50) { // Only save meaningful scroll positions
          scrollPositions.current.set(currentPath.current, scrollY);
          console.log('💾 Auto-saved scroll position for', currentPath.current, ':', scrollY);
        }
      }
    };

    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(saveScrollPosition, 300); // Debounce scroll saving
    };

    const handleBeforeUnload = () => {
      saveScrollPosition(); // Final save before leaving
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveScrollPosition(); // Save on cleanup
    };
  }, []);

  // Handle navigation and scroll restoration
  useEffect(() => {
    const newPath = location.pathname;
    
    // Skip initial load (React Router bug workaround)
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      currentPath.current = newPath;
      console.log('🏠 Initial page load:', newPath);
      return;
    }

    console.log('🎯 Navigation from', currentPath.current, 'to', newPath);
    console.log('🧭 Navigation type:', navigationType);
    
    // Update current path
    currentPath.current = newPath;

    if (navigationType === "POP") {
      console.log('⬅️ Back/Forward button pressed');
      const savedPosition = scrollPositions.current.get(newPath);
      
      if (savedPosition !== undefined && savedPosition > 50) {
        console.log('📍 Restoring scroll position to:', savedPosition);
        
        // Multiple restoration attempts with different timings
        const restoreScroll = (delay = 0) => {
          setTimeout(() => {
            window.scrollTo({ top: savedPosition, behavior: 'instant' });
            console.log('🔄 Attempted scroll to:', savedPosition, 'Current:', window.scrollY);
          }, delay);
        };
        
        restoreScroll(0);    // Immediate
        restoreScroll(50);   // After potential render
        restoreScroll(150);  // After content load
        restoreScroll(300);  // After async content
        
      } else {
        console.log('⬆️ No significant scroll position saved, scrolling to top');
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    } else {
      console.log('➡️ New navigation (link clicked) - scrolling to top');
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    console.log('💾 Current saved positions:', Object.fromEntries(scrollPositions.current));
    
  }, [location.pathname, navigationType]);

  return null;
};

export default ScrollToTop;